import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../config/URL";
import fetchAllCentersWithIds from "../List/CenterList";
import CustomDatePicker from "../CustomDatePicker";
import { format } from "date-fns";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "STUDENTREPORT";

const StudentReports = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter || "",
    studentName: "",
    status: "",
    courseType: "",
    startDate: "",
    endDate: "",
  });

  const [centerData, setCenterData] = useState(null);
  const [studentsData, setStudentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const defaultColumnVisibility = {};

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  // ✅ Fetch students with filters
  const fetchStudentReportsData = async (filterValues = filters) => {
    if (!filterValues.centerId) return;
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("centerId", filterValues.centerId);

      if (filterValues.studentName)
        params.append("studentName", filterValues.studentName);
      if (filterValues.status)
        params.append("studentStatus", filterValues.status);
      if (filterValues.courseType)
        params.append("courseType", filterValues.courseType);
      if (filterValues.startDate)
        params.append("startDate", filterValues.startDate);
      if (filterValues.endDate) params.append("endDate", filterValues.endDate);

      const response = await api.get(
        `globalStudentReport?${params.toString()}`,
      );

      if (response.data?.data) {
        setStudentData(response.data.data);
      } else {
        setStudentData([]);
      }
    } catch (error) {
      toast.error(error.message || "Error fetching data");
      setStudentData([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch centers
  const fetchCenterData = async () => {
    try {
      const centers = await fetchAllCentersWithIds();
      setCenterData(centers);

      const defaultCenterId =
        selectedCenter || (centers.length > 0 ? centers[0].id : "");
      setFilters((prevFilters) => ({
        ...prevFilters,
        centerId: defaultCenterId,
      }));

      // fetch data for default center
      fetchStudentReportsData({
        ...filters,
        centerId: defaultCenterId,
      });
    } catch (error) {
      toast.error(error.message || "Error fetching centers");
    }
  };

  // ✅ Table columns
  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        id: "__row_number",
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        size: 40,
        cell: ({ cell }) => (
          <span
            style={{ textAlign: "center" }}
            onClick={(e) => e.stopPropagation()}
          >
            {cell.getValue()}
          </span>
        ),
      },
      { accessorKey: "studentName", header: "Student Name" },
      { accessorKey: "birthDate", header: "Birth Date" },
      { accessorKey: "parentName", header: "Parent Name" },
      // { accessorKey: "mailingAddress", header: "Mailing Address" },
      { accessorKey: "email", header: "Email" },
      { accessorKey: "mobileNumber", header: "Mobile Number" },
      { accessorKey: "convertedBy", header: "Converted By Who" },
      { accessorKey: "course", header: "Course" },
      { accessorKey: "classCode", header: "Class Code" },
      { accessorKey: "className", header: "Class Name" },
      {
        accessorKey: "amountSpentToDate",
        header: "Amount Spent",
        cell: ({ cell }) => cell.getValue()?.toLocaleString(),
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: studentsData,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigate(`/student/view/${row.original.studentId}`),
      style: { cursor: "pointer" },
    }),
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, studentsData, "Student.csv")
        }
      >
        Export CSV
      </button>
    ),
  });

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": { color: "#eb862a" },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": { color: "#eb862a" },
            "&.Mui-checked + .MuiSwitch-track": { backgroundColor: "#eb862a" },
          },
        },
      },
    },
  });

  // ✅ Updated to handle both normal input & date picker
  const handleFilterChange = (eOrName, value) => {
    if (typeof eOrName === "string") {
      setFilters((prev) => ({ ...prev, [eOrName]: value }));
    } else {
      const { name, value } = eOrName.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const applyFilters = () => {
    fetchStudentReportsData(filters);
  };

  const clearFilter = () => {
    const resetFilters = {
      centerId: selectedCenter || "",
      studentName: "",
      status: "",
      courseType: "",
      startDate: "",
      endDate: "",
    };
    setFilters(resetFilters);
    fetchStudentReportsData(resetFilters);
  };

  useEffect(() => {
    fetchCenterData();
  }, []);

  return (
    <div className="container-fluid my-4 center">
      <ol
        className="breadcrumb my-3 px-2"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Student Report<span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Student Report
        </li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="me-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Student Report
              </span>
            </span>
          </div>
        </div>
        <div className="mb-3">
          <div className="individual_fliters d-lg-flex ">
            <div className="form-group mb-0 ms-2 mb-1">
              <select
                className="form-select form-select-sm"
                name="centerId"
                onChange={handleFilterChange}
                value={filters.centerId}
              >
                <option value="" disabled>
                  Select a Center
                </option>
                {centerData?.map((center) => (
                  <option key={center.id} value={center.id}>
                    {center.centerNames}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="studentName"
                value={filters.studentName}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                placeholder="Student Name"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <select
                className="form-select form-select-sm center_list"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">Select Status</option>
                <option value="PURSUING">PURSUING</option>
                <option value="END_CLASS">END CLASS</option>
                <option value="YET_TO_START">YET TO START</option>
                <option value="COMPLETED">COMPLETED</option>
                <option value="GRADUATE">GRADUATE</option>
                <option value="SUSPEND">SUSPEND</option>
                <option value="WITHDRAWN">WITHDRAWN</option>
                {/* <option value="Withdrawn">Withdrawn</option>
                <option value="Active">Active</option>
                <option value="Waiting">Waiting</option> */}
              </select>
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <select
                className="form-select form-select-sm center_list"
                name="courseType"
                value={filters.courseType}
                onChange={handleFilterChange}
              >
                <option value="">Select Course Type</option>
                <option value="Holiday">Holiday</option>
                <option value="Normal">Normal</option>
              </select>
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div className="individual_fliters d-lg-flex mt-2">
              <div className="form-group mb-0 mx-2 mb-1">
                <CustomDatePicker
                  dateFormat="yyyy-MM-dd"
                  className="form-control form-control-sm center_list ps-5"
                  name="startDate"
                  value={filters.startDate}
                  onChange={(date) =>
                    handleFilterChange(
                      "startDate",
                      date ? format(date, "yyyy-MM-dd") : "",
                    )
                  }
                  placeholderText="Start Date"
                />
              </div>
              <div className="form-group mb-0 mb-1">
                <CustomDatePicker
                  dateFormat="yyyy-MM-dd"
                  className="form-control form-control-sm center_list ps-5"
                  name="endDate"
                  value={filters.endDate}
                  onChange={(date) =>
                    handleFilterChange(
                      "endDate",
                      date ? format(date, "yyyy-MM-dd") : "",
                    )
                  }
                  placeholderText="End Date"
                />
              </div>

              <div className="form-group mb-0 ms-2 mb-1">
                <button
                  type="button"
                  style={{ background: "#eb862a" }}
                  className="btn btn-sm text-white"
                  onClick={applyFilters}
                >
                  Search
                </button>
              </div>
              <div className="form-group mb-0 ms-2 mb-1">
                <button
                  type="button"
                  className="btn btn-sm btn-border"
                  onClick={clearFilter}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <ThemeProvider theme={theme}>
            <MaterialReactTable table={table} />
          </ThemeProvider>
        )}
      </div>
    </div>
  );
};

export default StudentReports;
